import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { Button, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';

import useVitoloClaims from '../../useVitoloClaims';
import {
  usePrefillSomeFormFieldsForExistingVitoloClaim,
  useUploadTreatmentDocumentVitoloBackButtonLink,
  useUploadTreatmentDocumentVitoloNextButtonLink,
} from './uploadTreatmentDocumentVitoloHooks';
import {
  VITOLO_DOCUMENT_TYPE,
  VITOLO_IS_EXISTING_CLAIM_ANSWER,
  useNewVitoloFormDataValue,
} from '../../claimFormData/newClaimFormData';

import { BackButton, PageTitle } from '../../../../components/common';
import ButtonContainer from '../../../../components/ButtonContainer';

import FileUploader from './FileUploader';
import FileList from './FileList';

import { StyledVerticalContent, StyledFileUploadTipContainer } from './FileUploader.styled';

export default function UploadTreatmentDocumentVitolo(props) {
  const { selectedDocuments, setSelectedDocuments, setClaimSubmitted, claimsCount } = props;

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('desktop'));

  const validSelectedDocuments = Array.isArray(selectedDocuments)
    ? selectedDocuments.filter((d) => d?.valid)
    : [];

  const history = useHistory();
  const { claimId } = useParams();

  const [submitVitoloClaim, { loading: submitting }] = useVitoloClaims(claimId);

  const nextButtonLink = useUploadTreatmentDocumentVitoloNextButtonLink(claimId);

  const addFileHandler = (currFiles) => {
    setSelectedDocuments((state) => [...state, ...currFiles]);
  };

  const removeFileHandler = (ev, id) => {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }

    const updatedFileList = selectedDocuments?.filter((item) => item?.fileId !== id);
    setSelectedDocuments(updatedFileList);
  };

  const nextStepHandler = () => {
    history.push(nextButtonLink);
  };

  const handleSubmitClaim = () => {
    const payload = {
      files: validSelectedDocuments.map((doc) => doc.file),
    };

    submitVitoloClaim({
      payload,
      setSelectedDocuments,
      setClaimSubmitted,
      clearFormData: false,
    });
  };

  const hasFiles = selectedDocuments?.length > 0;

  const [documentType, setDocumentType] = useNewVitoloFormDataValue(VITOLO_DOCUMENT_TYPE);
  const [isExistingClaim] = useNewVitoloFormDataValue(VITOLO_IS_EXISTING_CLAIM_ANSWER);

  usePrefillSomeFormFieldsForExistingVitoloClaim(claimId, setDocumentType);

  const backButtonLink = useUploadTreatmentDocumentVitoloBackButtonLink(
    claimId,
    documentType,
    claimsCount,
    isExistingClaim
  );

  const shouldSubmitForm = !nextButtonLink;

  return (
    <>
      <BackButton
        label={
          <Typography variant="h3">
            <FormattedMessage id="common.button.back" />
          </Typography>
        }
        to={backButtonLink}
      />

      <PageTitle>
        <Typography variant="h1">
          <FormattedMessage
            id={
              hasFiles
                ? 'new_claim.upload.page.title.with.files'
                : 'new_claim.upload.page.title.without.files'
            }
          />
        </Typography>
      </PageTitle>

      <StyledVerticalContent>
        {!hasFiles && (
          <>
            <Typography variant="p">
              <FormattedMessage id="new_claim.upload.page.description" />
            </Typography>

            <StyledFileUploadTipContainer>
              <Typography variant="p">
                <FormattedMessage id="new_claim.upload.page.reminder" />
              </Typography>
            </StyledFileUploadTipContainer>
          </>
        )}

        {hasFiles && (
          <Paper>
            <FileList files={selectedDocuments} onRemove={removeFileHandler} />
          </Paper>
        )}

        <Paper>
          <FileUploader selectedFiles={selectedDocuments} setSelectedFiles={addFileHandler} />

          {isDesktop && hasFiles && (
            <ButtonContainer>
              {shouldSubmitForm ? (
                <Button
                  onClick={handleSubmitClaim}
                  disabled={submitting || !validSelectedDocuments.length}
                >
                  <Typography variant="buttons">
                    <FormattedMessage id="vitolo.new_claim.submit_claim_form.send_button" />
                  </Typography>
                </Button>
              ) : (
                <Button
                  onClick={nextStepHandler}
                  disabled={!validSelectedDocuments.length}
                  data-testid="next_button"
                >
                  <Typography variant="buttons">
                    <FormattedMessage id="common.button.next" />
                  </Typography>
                </Button>
              )}
            </ButtonContainer>
          )}
        </Paper>
      </StyledVerticalContent>

      {!isDesktop && hasFiles && (
        <ButtonContainer>
          {shouldSubmitForm ? (
            <Button
              onClick={handleSubmitClaim}
              disabled={submitting || !validSelectedDocuments.length}
            >
              <Typography variant="buttons">
                <FormattedMessage id="vitolo.new_claim.submit_claim_form.send_button" />
              </Typography>
            </Button>
          ) : (
            <Button
              onClick={nextStepHandler}
              disabled={!validSelectedDocuments.length}
              data-testid="next_button"
            >
              <Typography variant="buttons">
                <FormattedMessage id="common.button.next" />
              </Typography>
            </Button>
          )}
        </ButtonContainer>
      )}
    </>
  );
}

UploadTreatmentDocumentVitolo.propTypes = {
  selectedDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      fileId: PropTypes.string,
      valid: PropTypes.bool,
    })
  ),
  setSelectedDocuments: PropTypes.func,
};
